import React, { useState, useEffect, useRef, useCallback } from 'react';
import { 
  X, Search, Download, ExternalLink, ZoomIn, ZoomOut, 
  Maximize, Minimize, Highlighter, AlertCircle, 
  ChevronLeft, ChevronRight, PanelLeft, PanelLeftClose
} from 'lucide-react';

// Importation de la fonction askGPT
import { askGPT } from '../../components/OpenAIService'; // Assurez-vous que le chemin est correct

// Importer les modules personnalisés
import usePassageManager from './PassageManager';
import usePDFNavigator from './usePDFNavigator';
import PassagesPanel from './PassagesPanel';
import ConceptsPanel from './ConceptsPanel';

const SmartPDFViewer = ({ 
  documentUrl, 
  initialPassages = [], 
  onClose,
  documentId, 
  userId,
  competenceId,
  makeAuthenticatedRequest,
  onError,
  selectedPassage = null
}) => {
  // Références
  const iframeRef = useRef(null);
  const viewerContainerRef = useRef(null);
  const searchInputRef = useRef(null);
  const resizePanelRef = useRef(null);
  
  // URL du backend
  const BackEndUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8080';

  // États d'interface
  const [activeTab, setActiveTab] = useState(initialPassages.length > 0 ? 'passages' : 'document');
  const [isFullScreen, setIsFullScreen] = useState(false);
  const [showPassagesPanel, setShowPassagesPanel] = useState(true);
  const [isMobile, setIsMobile] = useState(false);
  const [selectedConcept, setSelectedConcept] = useState(null);
  const [showConceptDetails, setShowConceptDetails] = useState(false);
  const [conceptFilter, setConceptFilter] = useState(null);
  const [showSearchConcepts, setShowSearchConcepts] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  
  // Nouvel état pour la largeur du panneau de passages
  const [passagesPanelWidth, setPassagesPanelWidth] = useState(350); // Largeur par défaut
  const [isResizing, setIsResizing] = useState(false);
  
  // Nouvel état pour stocker les résumés des passages
  const [passageSummaries, setPassageSummaries] = useState({});
  // État pour suivre les résumés en cours de génération
  const [generatingSummaries, setGeneratingSummaries] = useState({});

  // Utiliser les hooks personnalisés
  const {
    passages,
    filteredPassages,
    isSearching,
    initializePassages,
    updateFilteredPassages,
    searchInDocument,
    highlightPassageInViewer, // Fonction pour mettre en surbrillance le passage dans le PDF
    renderPassageContent
  } = usePassageManager(
    initialPassages, 
    makeAuthenticatedRequest, 
    BackEndUrl, 
    documentId, 
    userId, 
    competenceId, 
    onError
  );

  const {
    isLoading,
    setIsLoading, 
    zoomLevel,
    navigationAttempts,
    buildPdfViewerUrl,
    navigateToPage,
    zoomIn,
    zoomOut
  } = usePDFNavigator(
    documentUrl, 
    iframeRef, 
    selectedPassage?.id || null, 
    passages
  );

  // État du passage actif
  const [activePassageId, setActivePassageId] = useState(selectedPassage?.id || null);

  // Fonction pour trier les passages par numéro de page
  const sortedFilteredPassages = useCallback(() => {
    return [...filteredPassages].sort((a, b) => {
      // Convertir en nombre pour assurer un tri numérique
      const pageA = parseInt(a.pageNumber) || 0;
      const pageB = parseInt(b.pageNumber) || 0;
      return pageA - pageB;
    });
  }, [filteredPassages]);

  // Détecter l'écran mobile
  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth < 768;
      setIsMobile(mobile);
      if (mobile) {
        setShowPassagesPanel(false);
      }
    };
    
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Initialiser les passages
  useEffect(() => {
    const enrichedPassages = initializePassages(initialPassages);
    
    if (selectedPassage) {
      setActivePassageId(selectedPassage.id);
    } else if (enrichedPassages && enrichedPassages.length > 0) {
      setActivePassageId(enrichedPassages[0].id);
    }
  }, [initialPassages, selectedPassage, initializePassages]);

  // Nouvelle fonction pour générer le résumé d'un passage
  const generatePassageSummary = useCallback(async (passage) => {
    if (!passage || !passage.id || !passage.content) {
      console.error("Passage invalide pour la génération de résumé");
      return null;
    }

    // Vérifier si un résumé existe déjà ou est en cours de génération
    if (passageSummaries[passage.id]) {
      return passageSummaries[passage.id];
    }
    
    if (generatingSummaries[passage.id]) {
      return "Génération du résumé en cours...";
    }

    // Marquer ce passage comme étant en cours de résumé
    setGeneratingSummaries(prev => ({
      ...prev,
      [passage.id]: true
    }));

    try {
      const prompt = `Résume ce passage de texte en 2-3 phrases concises en français. 
      Concentre-toi uniquement sur les informations essentielles et les idées principales. 
      Le résumé doit être facile à comprendre et direct. 
      Passage: "${passage.content}"`;

      const summary = await askGPT(prompt);
      
      // Stocker le résumé généré
      setPassageSummaries(prev => ({
        ...prev,
        [passage.id]: summary
      }));
      
      return summary;
    } catch (error) {
      console.error("Erreur lors de la génération du résumé:", error);
      return "Impossible de générer un résumé pour ce passage.";
    } finally {
      // Marquer que la génération est terminée
      setGeneratingSummaries(prev => {
        const newState = { ...prev };
        delete newState[passage.id];
        return newState;
      });
    }
  }, [passageSummaries, generatingSummaries]);

  // Gestionnaire pour le redimensionnement du panneau
  const startResizing = useCallback((e) => {
    e.preventDefault();
    setIsResizing(true);
  }, []);

  const stopResizing = useCallback(() => {
    setIsResizing(false);
  }, []);

  const resizePanel = useCallback((e) => {
    if (isResizing) {
      // Limiter la largeur entre 200px et 50% de la largeur de la fenêtre
      const minWidth = 200;
      const maxWidth = window.innerWidth * 0.5;
      const newWidth = Math.max(minWidth, Math.min(maxWidth, e.clientX));
      setPassagesPanelWidth(newWidth);
    }
  }, [isResizing]);

  // Ajouter les gestionnaires d'événements pour le redimensionnement
  useEffect(() => {
    window.addEventListener('mousemove', resizePanel);
    window.addEventListener('mouseup', stopResizing);
    
    return () => {
      window.removeEventListener('mousemove', resizePanel);
      window.removeEventListener('mouseup', stopResizing);
    };
  }, [resizePanel, stopResizing]);

  // Navigation entre passages du même concept
  const navigateToConceptPassage = useCallback((direction) => {
    if (!activePassageId || !conceptFilter) return;
    
    const conceptPassages = filteredPassages.filter(p => p.concept === conceptFilter);
    if (conceptPassages.length <= 1) return;
    
    const currentIndex = conceptPassages.findIndex(p => p.id === activePassageId);
    if (currentIndex === -1) return;
    
    const nextIndex = direction === 'next' 
      ? (currentIndex + 1) % conceptPassages.length
      : (currentIndex - 1 + conceptPassages.length) % conceptPassages.length;
    
    const targetPassage = conceptPassages[nextIndex];
    navigateToPage(targetPassage.pageNumber);
    setActivePassageId(targetPassage.id);
    
    // Ajouter la mise en surbrillance du passage
    highlightPassageInViewer(targetPassage);
  }, [activePassageId, conceptFilter, filteredPassages, navigateToPage, highlightPassageInViewer]);

  // Gestion du plein écran
  const toggleFullScreen = () => {
    const element = viewerContainerRef.current || document.documentElement;
    
    if (!document.fullscreenElement) {
      element.requestFullscreen().catch(err => {
        console.error(`Erreur lors du passage en plein écran: ${err.message}`);
      });
      setIsFullScreen(true);
    } else {
      document.exitFullscreen();
      setIsFullScreen(false);
    }
  };

  // Toggle pour afficher/masquer le panneau des passages
  const togglePassagesPanel = () => {
    setShowPassagesPanel(!showPassagesPanel);
  };

  // Gestionnaires de recherche et navigation - Amélioré pour la recherche vectorielle
  const handleSearch = async (event) => {
    event?.preventDefault();
    if (!searchQuery.trim()) {
      updateFilteredPassages(passages, conceptFilter, showSearchConcepts);
      return;
    }
    
    try {
      // Amélioration de la recherche vectorielle
      const result = await searchInDocument(searchQuery);
      
      // Si la recherche est réussie mais ne donne pas assez de résultats, montrer un message
      if (result && Array.isArray(result) && result.length === 0) {
        // Vous pourriez ajouter un état pour afficher un message à l'utilisateur
        console.log("Aucun passage correspondant trouvé.");
      }
    } catch (error) {
      console.error("Erreur lors de la recherche:", error);
      onError(error, "Recherche dans le document");
    }
  };

  const navigateToPassage = (passage) => {
    if (!passage) return;
    
    setActivePassageId(passage.id);
    navigateToPage(passage.pageNumber);
    
    // Mettre en surbrillance le passage dans le PDF
    highlightPassageInViewer(passage);
    
    if (isMobile) {
      setActiveTab('document');
    }
  };

  // Réinitialiser la recherche
  const handleResetSearch = () => {
    setSearchQuery('');
    updateFilteredPassages(passages, conceptFilter, showSearchConcepts);
  };

  // Fonction modifiée pour rendre le contenu du passage avec résumé
  const renderPassageContentWithSummary = useCallback((passage) => {
    if (!passage) return null;
    
    // Si le résumé existe déjà, l'utiliser
    const summary = passageSummaries[passage.id];
    const isGenerating = generatingSummaries[passage.id];
    
    // Si le passage est sélectionné, afficher le contenu original avec style
    const isActive = passage.id === activePassageId;
    
    return (
      <div 
        className={`relative p-3 mb-2 rounded-lg border transition-all cursor-pointer ${isActive ? 'bg-blue-50 border-blue-300' : 'border-gray-200 hover:border-blue-200 hover:bg-blue-50/50'}`}
        onClick={() => navigateToPassage(passage)}
      >
        {/* Afficher le concept et le numéro de page */}
        <div className="flex justify-between items-center mb-2">
          <div className="flex items-center gap-2">
            {/* Numéro de page déplacé ici */}
            <span className="px-2 py-1 text-xs rounded-md bg-gray-100 text-gray-700 font-medium">
              Page {passage.pageNumber}
            </span>
            
            {passage.concept && (
              <span className="px-2 py-1 text-xs rounded-md bg-blue-100 text-blue-800">
                {passage.concept}
              </span>
            )}
          </div>
        </div>
        
        {/* Corps du passage: résumé ou indication de chargement */}
        <div className="text-sm text-gray-800 mb-2 line-clamp-4">
          {summary ? (
            summary
          ) : isGenerating ? (
            <div className="flex items-center gap-2">
              <div className="animate-pulse h-2 bg-gray-200 rounded w-3/4"></div>
              <div className="animate-pulse h-2 bg-gray-200 rounded w-1/2"></div>
            </div>
          ) : (
            // Si pas de résumé, déclencher la génération et afficher un état de chargement
            <div className="animate-pulse" onClick={() => generatePassageSummary(passage)}>
              <div className="h-2 bg-gray-200 rounded w-full mb-2"></div>
              <div className="h-2 bg-gray-200 rounded w-5/6 mb-2"></div>
              <div className="h-2 bg-gray-200 rounded w-3/4"></div>
            </div>
          )}
        </div>
        
        {/* Le bouton "Voir dans le document" a été supprimé */}
      </div>
    );
  }, [passageSummaries, generatingSummaries, activePassageId, navigateToPassage, generatePassageSummary]);

  // Générer les résumés pour les passages visibles initiaux
  useEffect(() => {
    if (filteredPassages.length > 0) {
      // Générer les résumés pour les 5 premiers passages
      const initialPassagesToSummarize = sortedFilteredPassages().slice(0, 5);
      initialPassagesToSummarize.forEach(passage => {
        if (!passageSummaries[passage.id] && !generatingSummaries[passage.id]) {
          generatePassageSummary(passage);
        }
      });
    }
  }, [filteredPassages, passageSummaries, generatingSummaries, generatePassageSummary, sortedFilteredPassages]);

  // Rendu du contenu pour les téléphones mobiles
  const renderMobileContent = () => {
    if (activeTab === 'passages') {
      return (
        <PassagesPanel
          passages={passages}
          filteredPassages={sortedFilteredPassages()}
          activePassageId={activePassageId}
          renderPassageContent={renderPassageContentWithSummary}
          onPassageSelect={navigateToPassage}
          onSearch={handleSearch}
          isSearching={isSearching}
          searchQuery={searchQuery}
          onSearchQueryChange={setSearchQuery}
          onResetSearch={handleResetSearch}
        />
      );
    } else {
      return renderPdfViewer();
    }
  };

  // Rendu du PDF Viewer
  const renderPdfViewer = () => {
    return (
      <div className="flex-grow flex flex-col">
        {/* Barre d'outils */}
        <div className="p-2 border-b border-gray-200 bg-gray-100 flex justify-between items-center">
          <div className="flex items-center space-x-2">
            {activePassageId && (
              <span className="text-xs sm:text-sm text-blue-600 flex items-center">
                <Highlighter className="h-4 w-4 mr-1" />
                {filteredPassages.find(p => p.id === activePassageId)?.concept 
                  ? `Concept: ${filteredPassages.find(p => p.id === activePassageId)?.concept} (Page ${filteredPassages.find(p => p.id === activePassageId)?.pageNumber || '?'})`
                  : `Passage sélectionné${filteredPassages.find(p => p.id === activePassageId)?.pageNumber ? ` (Page ${filteredPassages.find(p => p.id === activePassageId)?.pageNumber})` : ''}`}
              </span>
            )}
          </div>
          
          <div className="flex items-center space-x-2">
            {/* Contrôles de zoom */}
            <button onClick={zoomOut} className="p-1 rounded hover:bg-gray-200" title="Zoom arrière">
              <ZoomOut className="h-4 w-4" />
            </button>
            
            <span className="text-xs sm:text-sm">{zoomLevel}%</span>
            
            <button onClick={zoomIn} className="p-1 rounded hover:bg-gray-200" title="Zoom avant">
              <ZoomIn className="h-4 w-4" />
            </button>
            
            {/* Navigation entre passages du concept */}
            {conceptFilter && filteredPassages.filter(p => p.concept === conceptFilter).length > 1 && (
              <div className="ml-2 p-1 border rounded flex items-center">
                <button onClick={() => navigateToConceptPassage('prev')} className="p-1 rounded hover:bg-gray-200">
                  <ChevronLeft className="h-4 w-4" />
                </button>
                
                <span className="text-xs mx-1 text-gray-500">
                  {(() => {
                    const conceptPassages = filteredPassages.filter(p => p.concept === conceptFilter);
                    const currentIndex = conceptPassages.findIndex(p => p.id === activePassageId);
                    return `${currentIndex + 1}/${conceptPassages.length}`;
                  })()}
                </span>
                
                <button onClick={() => navigateToConceptPassage('next')} className="p-1 rounded hover:bg-gray-200">
                  <ChevronRight className="h-4 w-4" />
                </button>
              </div>
            )}
          </div>
        </div>
        
        {/* Iframe PDF */}
        <div className="flex-grow overflow-auto bg-gray-100 flex justify-center">
          <div className="h-full w-full overflow-auto flex justify-center">
            {/* Loader */}
            {isLoading && (
              <div className="absolute inset-0 flex items-center justify-center bg-white bg-opacity-75 z-40">
                <div className="p-10 text-center">
                  <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-600 mb-4"></div>
                  <p className="text-gray-600">Chargement du document...</p>
                </div>
              </div>
            )}
            
            {/* Notification de navigation */}
            {activePassageId && navigationAttempts > 0 && (
              <div className="absolute top-4 left-1/2 transform -translate-x-1/2 bg-blue-50 border border-blue-200 rounded-lg px-4 py-2 flex items-center gap-2 shadow-md z-40">
                <AlertCircle className="h-4 w-4 text-blue-500" />
                <span className="text-sm text-blue-700">
                  {navigationAttempts === 1 
                    ? `Navigation vers la page ${filteredPassages.find(p => p.id === activePassageId)?.pageNumber || '?'}...` 
                    : `Tentative ${navigationAttempts}/3 - Page ${filteredPassages.find(p => p.id === activePassageId)?.pageNumber || '?'}`}
                </span>
              </div>
            )}
            
            {/* Bouton pour afficher le panneau lorsqu'il est masqué (desktop uniquement) */}
            {!showPassagesPanel && !isMobile && (
              <button
                onClick={togglePassagesPanel}
                className="absolute top-24 left-4 p-2 bg-white rounded-full shadow-md hover:bg-gray-100 z-40"
                title="Afficher le panneau des passages"
              >
                <PanelLeft className="h-5 w-5 text-blue-600" />
              </button>
            )}
            
            {/* Iframe */}
            <iframe 
              ref={iframeRef}
              src={buildPdfViewerUrl(documentUrl)} 
              className="w-full h-full border-0"
              title="PDF Viewer"
              style={{ backgroundColor: '#f5f5f5'}}
              onLoad={() => {
                console.log("Iframe chargée - arrêt du chargement");
                // Marquer le chargement comme terminé dès que l'iframe est chargée
                setIsLoading(false);
                
                // Si un passage était actif, relancer la navigation après 1 seconde
                if (activePassageId) {
                  const activePassage = filteredPassages.find(p => p.id === activePassageId);
                  if (activePassage && activePassage.pageNumber) {
                    setTimeout(() => {
                      navigateToPage(activePassage.pageNumber);
                      
                      // Ajouter la mise en surbrillance du passage
                      highlightPassageInViewer(activePassage);
                    }, 1000);
                  }
                }
              }}
              onError={(e) => {
                console.error("Erreur lors du chargement du PDF:", e);
                setIsLoading(false);
              }}
            />
          </div>
        </div>
      </div>
    );
  };

  // Rendu principal
  return (
    <div ref={viewerContainerRef} className="fixed inset-0 bg-black bg-opacity-75 flex justify-center items-center z-50 overflow-hidden">
      <div className="relative w-full h-full max-w-7xl max-h-[90vh] bg-white flex flex-col rounded-lg">
        {/* En-tête */}
        <div className="flex items-center justify-between p-2 sm:p-4 border-b border-gray-200">
          <div className="flex items-center">
            <h2 className="text-lg font-medium">Visualisation du document</h2>
            {/* Bouton pour masquer le panneau des passages (desktop uniquement) */}
            {!isMobile && showPassagesPanel && (
              <button 
                onClick={togglePassagesPanel}
                className="ml-3 p-1 text-gray-500 hover:bg-gray-100 rounded-lg flex items-center"
                title="Masquer le panneau des passages"
              >
                <PanelLeftClose className="h-4 w-4 sm:h-5 sm:w-5" />
                <span className="ml-1 text-xs sm:text-sm">Masquer les passages</span>
              </button>
            )}
          </div>
          
          <div className="flex items-center space-x-1 sm:space-x-3">
            {/* Onglets mobiles */}
            {isMobile && (
              <div className="border rounded-md overflow-hidden flex">
                <button
                  className={`px-2 py-1 text-xs flex items-center ${activeTab === 'document' ? 'bg-blue-100 text-blue-700' : 'hover:bg-gray-100'}`}
                  onClick={() => setActiveTab('document')}
                >
                  Document
                </button>
                
                <button
                  className={`px-2 py-1 text-xs flex items-center ${activeTab === 'passages' ? 'bg-blue-100 text-blue-700' : 'hover:bg-gray-100'}`}
                  onClick={() => setActiveTab('passages')}
                >
                  <Search className="h-3 w-3 mr-1" />
                  Passages ({filteredPassages.length})
                </button>
              </div>
            )}
            
            {/* Actions */}
            <div className="flex items-center space-x-1 sm:space-x-2">
              {/* Boutons de téléchargement, nouvel onglet, plein écran, fermeture */}
              <a href={documentUrl} download className="px-2 sm:px-3 py-1 sm:py-1.5 text-xs sm:text-sm bg-green-50 text-green-600 rounded-lg hover:bg-green-100 transition-colors flex items-center">
                <Download className="h-3 w-3 sm:h-4 sm:w-4 mr-1" />
                <span className="hidden sm:inline">Télécharger</span>
              </a>
              
              <a href={buildPdfViewerUrl(documentUrl)} target="_blank" rel="noopener noreferrer" 
                className="px-2 sm:px-3 py-1 sm:py-1.5 text-xs sm:text-sm bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100 transition-colors flex items-center">
                <ExternalLink className="h-3 w-3 sm:h-4 sm:w-4 mr-1" />
                <span className="hidden sm:inline">Nouvel onglet</span>
              </a>
              
              <button onClick={toggleFullScreen} className="p-1 sm:p-2 text-gray-500 hover:bg-gray-100 rounded-lg">
                {isFullScreen ? <Minimize className="h-4 w-4" /> : <Maximize className="h-4 w-4" />}
              </button>
              
              <button onClick={onClose} className="p-1 sm:p-2 text-gray-500 hover:bg-gray-100 rounded-full">
                <X className="h-4 w-4 sm:h-5 sm:w-5" />
              </button>
            </div>
          </div>
        </div>
        
        {/* Contenu principal - Différent selon mobile/desktop */}
        {isMobile ? (
          // Contenu pour mobile - affiche soit le panneau des passages, soit le viewer
          <div className="flex-grow overflow-hidden">
            {renderMobileContent()}
          </div>
        ) : (
          // Contenu pour desktop - layout à deux colonnes
          <div className="flex-grow flex overflow-hidden">
            {/* Panneau des passages avec redimensionnement - affiché conditionnellement */}
            {showPassagesPanel && (
              <div 
                style={{ 
                  width: `${passagesPanelWidth}px`,
                  maxWidth: '50%',
                  position: 'relative'
                }}
                className="h-full flex flex-col bg-gray-50 transition-all ease-in-out"
              >
                <PassagesPanel
                  passages={passages}
                  filteredPassages={sortedFilteredPassages()}
                  activePassageId={activePassageId}
                  renderPassageContent={renderPassageContentWithSummary}
                  onPassageSelect={navigateToPassage}
                  onSearch={handleSearch}
                  isSearching={isSearching}
                  searchQuery={searchQuery}
                  onSearchQueryChange={setSearchQuery}
                  onResetSearch={handleResetSearch}
                />
                
                {/* Barre de redimensionnement */}
                <div
                  ref={resizePanelRef}
                  onMouseDown={startResizing}
                  className="absolute top-0 right-0 h-full w-1 bg-gray-300 hover:bg-blue-500 cursor-col-resize z-10"
                  style={{ 
                    opacity: isResizing ? 1 : 0.5 
                  }}
                />
              </div>
            )}
            
            {/* PDF Viewer - toujours affiché en mode desktop */}
            {renderPdfViewer()}
          </div>
        )}
        
        {/* Panneau de détails du concept sélectionné */}
        {showConceptDetails && selectedConcept && (
          <div className="absolute inset-0 z-10 flex">
            {/* Fond semi-transparent sur mobile seulement */}
            <div 
              className="md:hidden absolute inset-0 bg-black bg-opacity-50"
              onClick={() => setShowConceptDetails(false)}
            ></div>
            
            {/* Panneau latéral de détails */}
            <div className="relative ml-auto w-full md:w-1/3 lg:w-1/4 bg-white shadow-lg">
              <ConceptsPanel 
                selectedConcept={selectedConcept}
                onClose={() => setShowConceptDetails(false)}
                onNavigateToPage={(pageNumber) => {
                  navigateToPage(pageNumber);
                  setShowConceptDetails(false);
                }}
                onNavigateToPrevConceptPassage={() => navigateToConceptPassage('prev')}
                onNavigateToNextConceptPassage={() => navigateToConceptPassage('next')}
                conceptPassages={filteredPassages.filter(p => p.concept === selectedConcept.concept)}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SmartPDFViewer;